import React from 'react';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: September 25, 2025</p>

        <section className="policy-section">
          <h2>1. Introduction</h2>
          <p>
            Read The Word ("we," "our," or "us") operates the Read The Word mobile application 
            and web platform (the "Service"). This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you use our Service.
          </p>
          <p>
            Read The Word is a Bible reading tracking application designed to help individuals 
            and families track their Bible reading progress, participate in reading plans, and 
            engage with their church community.
          </p>
        </section>

        <section className="policy-section">
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <ul>
            <li><strong>Account Information:</strong> Email address, name, password</li>
            <li><strong>Profile Information:</strong> Age, gender (optional), church affiliation</li>
            <li><strong>User Preferences:</strong> Bible translation preference, competitive mode settings, app usage mode</li>
            <li><strong>Reading Preferences:</strong> Default reading method, audio speed, location, time of day, study aids</li>
          </ul>

          <h3>2.2 Reader Information</h3>
          <ul>
            <li><strong>Family Readers:</strong> Names, ages, and gender of family members you add to track their reading</li>
            <li><strong>Reading Data:</strong> Bible chapters read, reading dates, notes, comprehension levels</li>
            <li><strong>Detailed Reading Information:</strong> Translation used, reading method, duration, location, emotional impact, study aids used</li>
          </ul>

          <h3>2.3 App Usage Data</h3>
          <ul>
            <li><strong>Reading Reports:</strong> Bible chapters completed, reading dates, notes, and optional detailed reading information</li>
            <li><strong>Progress Data:</strong> Reading streaks, completion statistics, points earned, badges achieved</li>
            <li><strong>Gamification Data:</strong> Points, levels, badges, leaderboard participation (if competitive mode enabled)</li>
            <li><strong>Reading Plans:</strong> Selected reading plans and progress through them</li>
          </ul>

          <h3>2.4 Device and Technical Information</h3>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system, app version</li>
            <li><strong>Push Notification Tokens:</strong> Device tokens for sending notifications (if enabled)</li>
            <li><strong>Usage Analytics:</strong> App usage patterns, feature usage, error logs</li>
          </ul>

          <h3>2.5 Church and Community Data</h3>
          <ul>
            <li><strong>Church Information:</strong> Church name, membership status</li>
            <li><strong>Family Information:</strong> Family group memberships, invite codes</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>3. How We Use Your Information</h2>
          
          <h3>3.1 Core App Functionality</h3>
          <ul>
            <li>Provide and maintain the reading tracking service</li>
            <li>Track your Bible reading progress and generate statistics</li>
            <li>Enable family reading tracking and sharing</li>
            <li>Provide personalized reading plans and recommendations</li>
          </ul>

          <h3>3.2 Communication</h3>
          <ul>
            <li>Send account verification emails</li>
            <li>Send push notifications about reading reminders and progress (if enabled)</li>
            <li>Communicate important service updates</li>
            <li>Respond to your support requests</li>
          </ul>

          <h3>3.3 Social and Community Features</h3>
          <ul>
            <li>Enable family reading tracking and sharing</li>
            <li>Show leaderboards and compare progress (if competitive mode enabled)</li>
            <li>Connect you with your church community</li>
            <li>Award badges and points for achievements</li>
          </ul>

          <h3>3.4 Service Improvement</h3>
          <ul>
            <li>Analyze usage patterns to improve the app</li>
            <li>Debug technical issues and improve performance</li>
            <li>Develop new features based on user needs</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>4. Information Sharing and Disclosure</h2>
          
          <h3>4.1 Within Your Family Group</h3>
          <p>
            If you join or create a family group, other family members can see:
          </p>
          <ul>
            <li>Reader names and reading progress you've added</li>
            <li>Reading reports and statistics for family readers</li>
            <li>Notes and achievements (if shared)</li>
          </ul>

          <h3>4.2 Within Your Church Community</h3>
          <p>
            If you join a church and enable competitive features:
          </p>
          <ul>
            <li>Your reading progress may appear in church leaderboards</li>
            <li>Basic reading statistics may be shared for church-wide goals</li>
            <li>Your name and reading achievements may be visible to other church members</li>
          </ul>

          <h3>4.3 Service Providers</h3>
          <p>
            We may share information with third-party service providers who help us operate our service:
          </p>
          <ul>
            <li>Cloud hosting providers (for data storage)</li>
            <li>Email service providers (for account communications)</li>
            <li>Push notification services (Apple Push Notification Service)</li>
            <li>Analytics providers (for app improvement)</li>
          </ul>

          <h3>4.4 Legal Requirements</h3>
          <p>
            We may disclose your information if required to do so by law or in response to valid requests 
            by public authorities.
          </p>

          <h3>4.5 What We Don't Share</h3>
          <ul>
            <li>We do not sell your personal information to third parties</li>
            <li>We do not share your personal reading notes or detailed information without your consent</li>
            <li>We do not provide your information to marketing companies</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal information:
          </p>
          <ul>
            <li>Secure data transmission using encryption (HTTPS/TLS)</li>
            <li>Password hashing and secure authentication</li>
            <li>Regular security updates and monitoring</li>
            <li>Limited access to personal data on a need-to-know basis</li>
            <li>Secure cloud infrastructure with backup systems</li>
          </ul>
          <p>
            However, no method of transmission over the internet or electronic storage is 100% secure. 
            While we strive to protect your personal information, we cannot guarantee absolute security.
          </p>
        </section>

        <section className="policy-section">
          <h2>6. Your Privacy Rights and Choices</h2>
          
          <h3>6.1 Account Management</h3>
          <ul>
            <li><strong>Access:</strong> You can view and update your account information in the app settings</li>
            <li><strong>Correction:</strong> You can edit your profile, preferences, and reading data</li>
            <li><strong>Deletion:</strong> You can delete your account and all associated data</li>
          </ul>

          <h3>6.2 Privacy Controls</h3>
          <ul>
            <li><strong>Competitive Mode:</strong> You can disable competitive features to opt out of leaderboards</li>
            <li><strong>Family Sharing:</strong> You can leave family groups or adjust sharing preferences</li>
            <li><strong>Church Participation:</strong> You can leave church groups</li>
            <li><strong>Notifications:</strong> You can disable push notifications in your device settings</li>
          </ul>

          <h3>6.3 Data Export</h3>
          <p>
            You can request an export of your personal data by contacting us at the email address below.
          </p>
        </section>

        <section className="policy-section">
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to provide our services and fulfill 
            the purposes outlined in this Privacy Policy:
          </p>
          <ul>
            <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
            <li><strong>Inactive Accounts:</strong> Account data may be deleted after extended periods of inactivity</li>
            <li><strong>Legal Requirements:</strong> Some data may be retained longer if required by law</li>
            <li><strong>Account Deletion:</strong> When you delete your account, we remove your personal data within 30 days</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>8. Children's Privacy</h2>
          <p>
            Our Service is designed for family use, including tracking reading for children. When you add 
            family members (including children) as readers:
          </p>
          <ul>
            <li>You are responsible for obtaining any necessary consent</li>
            <li>Children's reading data is only accessible to family members</li>
            <li>We do not knowingly collect personal information directly from children under 13</li>
            <li>Parents can manage and delete their children's reading data at any time</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own. 
            We ensure that such transfers are conducted in accordance with applicable data protection laws 
            and include appropriate safeguards to protect your personal information.
          </p>
        </section>

        <section className="policy-section">
          <h2>10. Third-Party Services</h2>
          <p>
            Our app may contain links to third-party websites or integrate with third-party services. 
            This Privacy Policy does not apply to those third-party services. We encourage you to review 
            the privacy policies of any third-party services you access through our app.
          </p>
        </section>

        <section className="policy-section">
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by:
          </p>
          <ul>
            <li>Posting the new Privacy Policy in the app</li>
            <li>Sending you an email notification</li>
            <li>Providing a prominent notice in the app</li>
          </ul>
          <p>
            Your continued use of the Service after any changes indicates your acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section className="policy-section">
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
            please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> privacy@readtheword.app</p>
            <p><strong>Developer:</strong> Saltmarsh Digital</p>
            <p><strong>Address:</strong> [Your Address - Update as needed]</p>
          </div>
          <p>
            We will respond to your inquiries within a reasonable timeframe.
          </p>
        </section>

        <section className="policy-section">
          <h2>13. Legal Basis for Processing (EU Users)</h2>
          <p>
            If you are located in the European Union, our legal basis for processing your personal information includes:
          </p>
          <ul>
            <li><strong>Consent:</strong> Where you have given clear consent for specific purposes</li>
            <li><strong>Contract:</strong> To provide the services you've requested</li>
            <li><strong>Legitimate Interest:</strong> To improve our services and communicate with you</li>
            <li><strong>Legal Obligation:</strong> Where required by law</li>
          </ul>
        </section>

        <footer className="policy-footer">
          <p>
            This Privacy Policy is effective as of September 25, 2025. By using Read The Word, 
            you acknowledge that you have read and understood this Privacy Policy.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
